/**
 * v1 Item catalog — the MK-style "Question Box" loadout system.
 *
 * <p>The Stake screen ("Gear up for the spin") is being replaced by an
 * item-roll moment: between rounds, three slot reels spin per player and
 * land on randomized items drawn from a position-weighted table.
 * See <code>plans/gear-up-for-the-purrfect-biscuit.md</code> for the
 * design rationale and phased rollout.
 *
 * <p>This file encodes the full v1 catalog (20 items across four tiers)
 * but most of it is forward-looking. Phase 1 (the prove-out) only ships
 * the six items in {@link PHASE_1_ITEM_POOL} — equal-weight RNG, no
 * targeting, no chaos tier, no Final Lap. The rest of the catalog is
 * here so the data foundation is in place when phases 2 / 3 turn it on.
 *
 * <p>All glyphs in this file are placeholder emoji. The design pass in
 * phase 3 replaces them with a custom icon set.
 */

/** Stable wire id for an item. */
export type ItemId =
  // Common (leader-skewed)
  | 'COIN_STASH'
  | 'SLIP'
  | 'BOOST'
  | 'SHIELD'
  | 'RE_ROLL'
  // Uncommon
  | 'HIT'
  | 'X2_BONUS'
  | 'GEM_DROP'
  | 'TRAP'
  | 'TRIPLE_BOOST'
  | 'SHAKEDOWN'
  // Rare (chaos)
  | 'BOMB'
  | 'LUCKY_STAR'
  | 'POW'
  | 'SMOKESCREEN'
  | 'GOLD_RUSH'
  // Legendary (underdog-skewed)
  | 'ROCKET'
  | 'LIGHTNING'
  | 'CRAZY_EIGHT'
  | 'HEIST'
  // Phase 2 effect-redesign — added 2026-05-14
  | 'MAGNET'
  | 'BONUS_WEDGE';

/** Rarity tier — drives reveal animation, audio sting, and rubber-band
 *  draw weights. */
export type ItemTier = 'COMMON' | 'UNCOMMON' | 'RARE' | 'LEGENDARY';

/**
 * When an item's effect resolves. Determines how the inventory HUD
 * displays the item, and whether tap-to-deploy is needed.
 *
 * <ul>
 *   <li><b>PERSISTENT</b> — Coin Stash, Gem Drop, XP boosts. Apply
 *       immediately on roll, never sit in the inventory.</li>
 *   <li><b>INSTANT</b> — Shakedown, Lightning, POW, Heist. Fire the
 *       moment the player taps deploy; effect lands now.</li>
 *   <li><b>LOADED</b> — Trap, Boost, Bomb, Slip. Deploy now, resolve
 *       at the next wheel spin.</li>
 *   <li><b>AUTO_BUFF</b> — Lucky Star. Auto-fires on the player at
 *       wheel resolve; no deploy gesture.</li>
 *   <li><b>REACTIVE</b> — Shield. Auto-trigger when the player is
 *       targeted; never a manual fire.</li>
 *   <li><b>META</b> — Re-roll. Modifies the player's other inventory
 *       slots; doesn't fire at any game event.</li>
 * </ul>
 */
export type ItemFireWindow =
  | 'PERSISTENT'
  | 'INSTANT'
  | 'LOADED'
  | 'AUTO_BUFF'
  | 'REACTIVE'
  | 'META';

/** Full per-item record. */
export interface ItemMeta {
  id: ItemId;
  /** Player-facing display name (Title Case). */
  name: string;
  tier: ItemTier;
  fireWindow: ItemFireWindow;
  /** Single-character emoji glyph — placeholder pending design pass. */
  glyph: string;
  /** One-liner shown on the inventory-tile tooltip. */
  description: string;
  /** True when the player must pick a rival before the item fires. */
  targetingRequired: boolean;
}

/** v1 catalog. Order is canonical for any UI that lists items in
 *  catalog order (e.g. compendium). */
export const ITEMS: Record<ItemId, ItemMeta> = {
  // ----- COMMON -----
  COIN_STASH: {
    id: 'COIN_STASH',
    name: 'Coin Stash',
    tier: 'COMMON',
    fireWindow: 'PERSISTENT',
    glyph: '🪙',
    description: '+50 coins to your bank.',
    targetingRequired: false,
  },
  SLIP: {
    id: 'SLIP',
    name: 'Slip',
    tier: 'COMMON',
    fireWindow: 'LOADED',
    glyph: '🍌',
    description: 'Drop on the wheel — if it lands on the slip, you take that sliver.',
    targetingRequired: false,
  },
  BOOST: {
    id: 'BOOST',
    name: 'Boost',
    tier: 'COMMON',
    fireWindow: 'LOADED',
    glyph: '🥈',
    description: '+10% your slice this round.',
    targetingRequired: false,
  },
  SHIELD: {
    id: 'SHIELD',
    name: 'Shield',
    tier: 'COMMON',
    fireWindow: 'REACTIVE',
    glyph: '🛡',
    description: 'Blocks one item aimed at you.',
    targetingRequired: false,
  },
  RE_ROLL: {
    id: 'RE_ROLL',
    name: 'Re-roll',
    tier: 'COMMON',
    fireWindow: 'META',
    glyph: '🎲',
    description: 'Re-roll one of your other two slots once.',
    targetingRequired: false,
  },

  // ----- UNCOMMON -----
  HIT: {
    id: 'HIT',
    name: 'Hit',
    tier: 'UNCOMMON',
    fireWindow: 'LOADED',
    glyph: '🟢',
    description: 'A random rival loses a slice.',
    targetingRequired: false,
  },
  X2_BONUS: {
    id: 'X2_BONUS',
    name: 'X2 Bonus',
    tier: 'UNCOMMON',
    fireWindow: 'LOADED',
    glyph: '✖',
    description: 'Your winnings doubled this round.',
    targetingRequired: false,
  },
  GEM_DROP: {
    id: 'GEM_DROP',
    name: 'Gem Drop',
    tier: 'UNCOMMON',
    fireWindow: 'PERSISTENT',
    glyph: '💎',
    description: '+1 Blue Gem to your collection.',
    targetingRequired: false,
  },
  TRAP: {
    id: 'TRAP',
    name: 'Trap',
    tier: 'UNCOMMON',
    fireWindow: 'LOADED',
    glyph: '🪤',
    description: "Place on a rival's slice — if the wheel lands, you steal it.",
    targetingRequired: true,
  },
  TRIPLE_BOOST: {
    id: 'TRIPLE_BOOST',
    name: 'Triple Boost',
    tier: 'UNCOMMON',
    fireWindow: 'LOADED',
    glyph: '📐',
    description: '+30% this round, or split +10% across the next 3 rounds.',
    targetingRequired: false,
  },
  SHAKEDOWN: {
    id: 'SHAKEDOWN',
    name: 'Shakedown',
    tier: 'UNCOMMON',
    fireWindow: 'INSTANT',
    glyph: '👻',
    description: "Swipe coins from a rival's pot to yours, instantly.",
    targetingRequired: true,
  },

  // ----- RARE (chaos) -----
  BOMB: {
    id: 'BOMB',
    name: 'Bomb',
    tier: 'RARE',
    fireWindow: 'LOADED',
    glyph: '💣',
    description: "Auto-targets the leader's biggest slice. Visible fuse.",
    targetingRequired: false,
  },
  LUCKY_STAR: {
    id: 'LUCKY_STAR',
    name: 'Lucky Star',
    tier: 'RARE',
    fireWindow: 'AUTO_BUFF',
    glyph: '🌟',
    description: 'Immune to incoming items, and your slice doubled this round.',
    targetingRequired: false,
  },
  POW: {
    id: 'POW',
    name: 'POW',
    tier: 'RARE',
    fireWindow: 'INSTANT',
    glyph: '💥',
    description: 'Every rival loses half their reach this round.',
    targetingRequired: false,
  },
  SMOKESCREEN: {
    id: 'SMOKESCREEN',
    name: 'Smokescreen',
    tier: 'RARE',
    fireWindow: 'LOADED',
    glyph: '🌫',
    description: "Rivals can't see slice values for 3s before the spin.",
    targetingRequired: false,
  },
  GOLD_RUSH: {
    id: 'GOLD_RUSH',
    name: 'Gold Rush',
    tier: 'RARE',
    fireWindow: 'LOADED',
    glyph: '🍄',
    description: 'Multiplier active for the whole next round, not just one spin.',
    targetingRequired: false,
  },

  // ----- LEGENDARY (underdog-skewed) -----
  ROCKET: {
    id: 'ROCKET',
    name: 'Rocket',
    tier: 'LEGENDARY',
    fireWindow: 'LOADED',
    glyph: '🚀',
    description: 'Slice +50% AND triple coins next round.',
    targetingRequired: false,
  },
  LIGHTNING: {
    id: 'LIGHTNING',
    name: 'Lightning',
    tier: 'LEGENDARY',
    fireWindow: 'INSTANT',
    glyph: '⚡',
    // Producer brief 2026-05-16 — LIGHTNING is now a MASS TRANSFER
    // (was: pure damage). Rivals lose half, you collect the lot.
    description: "Steal half of every rival's winnings this round.",
    targetingRequired: false,
  },
  CRAZY_EIGHT: {
    id: 'CRAZY_EIGHT',
    name: 'Crazy Eight',
    tier: 'LEGENDARY',
    fireWindow: 'LOADED',
    glyph: '🎰',
    description: 'Eight items spin in sequence — one fires per round for 8 rounds.',
    targetingRequired: false,
  },
  HEIST: {
    id: 'HEIST',
    name: 'Heist',
    tier: 'LEGENDARY',
    fireWindow: 'INSTANT',
    glyph: '👑',
    description: 'Swap token totals with the current leader. One-time, ruinous.',
    targetingRequired: false,
  },

  // ----- PHASE 2 EFFECT-REDESIGN (2026-05-14) -----
  // New items added below the existing catalog to keep enum-name
  // stability for replay decode. Tier placement matches the
  // brainstorm in the plan file: MAGNET is a strong self-gain
  // (UNCOMMON tier), BONUS_WEDGE is the shared-lottery effect
  // (RARE — high upside, lottery-payoff).
  MAGNET: {
    id: 'MAGNET',
    name: 'Magnet',
    tier: 'UNCOMMON',
    fireWindow: 'LOADED',
    glyph: '🧲',
    description: 'Your pointer locks onto the wheel’s top wedge this round.',
    targetingRequired: false,
  },
  BONUS_WEDGE: {
    id: 'BONUS_WEDGE',
    name: 'Bonus Wedge',
    tier: 'RARE',
    fireWindow: 'LOADED',
    glyph: '✨',
    description: 'A high-value wedge appears on the wheel — anyone can land on it.',
    targetingRequired: false,
  },
};

/** Items grouped by tier. Order within each tier matches the catalog. */
export const ITEMS_BY_TIER: Record<ItemTier, ReadonlyArray<ItemId>> = {
  COMMON:    ['COIN_STASH', 'SLIP', 'BOOST', 'SHIELD', 'RE_ROLL'],
  UNCOMMON:  ['HIT', 'X2_BONUS', 'GEM_DROP', 'TRAP', 'TRIPLE_BOOST', 'SHAKEDOWN', 'MAGNET'],
  RARE:      ['BOMB', 'LUCKY_STAR', 'POW', 'SMOKESCREEN', 'GOLD_RUSH', 'BONUS_WEDGE'],
  LEGENDARY: ['ROCKET', 'LIGHTNING', 'CRAZY_EIGHT', 'HEIST'],
};

/** Catalog order for compendium / debug listings. */
export const ALL_ITEMS: ReadonlyArray<ItemId> = [
  ...ITEMS_BY_TIER.COMMON,
  ...ITEMS_BY_TIER.UNCOMMON,
  ...ITEMS_BY_TIER.RARE,
  ...ITEMS_BY_TIER.LEGENDARY,
];

/** Phase-1 prove-out pool. Six items, equal-weight RNG, no rubber-banding,
 *  no chaos tier, no targeting required. The minimum needed to felt-test
 *  whether the slot-roll-as-stake moment feels exciting on a real device.
 *  Phases 2+ replace this with the position-weighted draw tables in
 *  {@link RUBBER_BAND_TABLE}. */
export const PHASE_1_ITEM_POOL: ReadonlyArray<ItemId> = [
  'COIN_STASH',
  'SLIP',
  'BOOST',
  'SHIELD',
  'HIT',
  'X2_BONUS',
];

/** Tier display metadata — drives inventory-tile frame color, reveal-
 *  animation choice, and audio sting. */
export const ITEM_TIER_META: Record<
  ItemTier,
  { label: string; accent: string; rarityRank: number }
> = {
  COMMON:    { label: 'Common',    accent: '#9DA9C0', rarityRank: 1 },
  UNCOMMON:  { label: 'Uncommon',  accent: '#9DE54E', rarityRank: 2 },
  RARE:      { label: 'Rare',      accent: '#6BCBFF', rarityRank: 3 },
  LEGENDARY: { label: 'Legendary', accent: '#B475FF', rarityRank: 4 },
};

/**
 * Rubber-band draw table. Per-tier probabilities by player standing —
 * consulted each round via {@link rollLoadoutForPosition} (currently
 * client-side; server-side mirror lands in phase-2 wrap-up after the
 * felt-test gate passes).
 *
 * <p>Rows are tuples of (common, uncommon, rare, legendary). Sums to
 * 1.0 within each row. Standings beyond the table's length fall back
 * to the last (most-rubber-banded) row via
 * {@link tierWeightsForPosition}.
 */
export const RUBBER_BAND_TABLE: ReadonlyArray<{
  position: number;
  weights: readonly [number, number, number, number];
}> = [
  { position: 1, weights: [0.70, 0.25, 0.05, 0.00] },
  { position: 2, weights: [0.50, 0.35, 0.13, 0.02] },
  { position: 3, weights: [0.35, 0.40, 0.20, 0.05] },
  { position: 4, weights: [0.20, 0.35, 0.30, 0.15] },
];

/** Cap to prevent legendary-stacking even at last place. */
export const MAX_LEGENDARIES_PER_ROLL = 1;

/**
 * Final Lap rubber-band draw table. Activates at round
 * {@link FINAL_LAP_TRIGGER_ROUND}+ (the last two rounds of the
 * canonical 8-round match). Every position is guaranteed
 * Rare-or-better — common + uncommon weights drop to zero —
 * dramatically raising the chaos floor for the climax.
 *
 * <p>Rubber-band shape is preserved: last-place still skews more
 * toward Legendary than the leader, but even the leader has a real
 * chance of one. The {@link MAX_LEGENDARIES_PER_ROLL} cap still
 * applies, so a 4th-place roll can't stack three Legendaries.
 *
 * <p>This is the data side of the Final Lap signature beat described
 * in <code>plans/gear-up-for-the-purrfect-biscuit.md §7</code>. The
 * visual side (overlay, music swap, lap-counter celebration) lives in
 * the mobile {@code FinalLapOverlay} component.
 */
export const FINAL_LAP_RUBBER_BAND_TABLE: ReadonlyArray<{
  position: number;
  weights: readonly [number, number, number, number];
}> = [
  { position: 1, weights: [0.00, 0.00, 0.85, 0.15] },
  { position: 2, weights: [0.00, 0.00, 0.70, 0.30] },
  { position: 3, weights: [0.00, 0.00, 0.50, 0.50] },
  { position: 4, weights: [0.00, 0.00, 0.30, 0.70] },
];

/**
 * Round number at which Final Lap activates. Matches start at round 1
 * and run through round {@link MATCH_LENGTH_ROUNDS}, so triggering at
 * 7 covers rounds 7 and 8 — the last two of the eight-round arc.
 */
export const FINAL_LAP_TRIGGER_ROUND = 7;

/** Canonical match length in rounds. Encoded here for the rubber-band
 *  helpers that check Final Lap territory; the wheel state machine is
 *  the source of truth for actual round progression. */
export const MATCH_LENGTH_ROUNDS = 8;

/**
 * True when the given match round is in Final Lap territory.
 * Drives both the loadout-table swap (every player guaranteed
 * Rare-or-better) and the visual / audio "Final Lap!" beat.
 */
export function isFinalLap(matchNumber: number): boolean {
  return matchNumber >= FINAL_LAP_TRIGGER_ROUND;
}

// ---------------------------------------------------------------------
// Phase-2 rolling logic
//
// These helpers turn the rubber-band table into actual loadouts. They
// run client-side in phase 2's prove-out (the same way phase 1 did for
// equal-weight rolls). When the felt-test gate passes, the same logic
// is mirrored on the server inside the ROLL_LOADOUT command so the
// client receives deterministic, non-manipulatable results.
// ---------------------------------------------------------------------

/**
 * Minimal seat shape needed for {@link rankSeatsByTokens}. Kept as a
 * structural type so this module stays independent of the wire types
 * defined in {@code index.ts} (avoids a circular import).
 */
export interface RankableSeat {
  seatId: number;
  tokens: number;
  /** Wire-equivalent of {@code WheelSeatKind}; loose to avoid the
   *  circular import. EMPTY seats are excluded from ranking. */
  kind: string;
}

/**
 * Returns a map of {@code seatId → 1-indexed standing} (1 = leader,
 * larger = lower). EMPTY seats are excluded. Ties are broken by
 * {@code seatId} ascending so the curve is stable across renders even
 * when token totals are equal.
 */
export function rankSeatsByTokens(
  seats: ReadonlyArray<RankableSeat>,
): ReadonlyMap<number, number> {
  const playable = seats.filter((s) => s.kind !== 'EMPTY');
  const sorted = [...playable].sort((a, b) => {
    if (b.tokens !== a.tokens) return b.tokens - a.tokens;
    return a.seatId - b.seatId;
  });
  const out = new Map<number, number>();
  sorted.forEach((s, i) => out.set(s.seatId, i + 1));
  return out;
}

/**
 * Returns the four-tuple of tier weights (common, uncommon, rare,
 * legendary) for a given player standing. Standings beyond the
 * table's length fall back to the last (most-rubber-banded) row, so
 * five- and six-seat rooms still get a sensible curve at the bottom.
 *
 * <p>When {@code finalLap} is true, the {@link FINAL_LAP_RUBBER_BAND_TABLE}
 * is consulted instead — this is the signature "every player guaranteed
 * Rare-or-better" raise-the-floor table that activates at round 7+.
 */
export function tierWeightsForPosition(
  position: number,
  finalLap = false,
): readonly [number, number, number, number] {
  const table = finalLap ? FINAL_LAP_RUBBER_BAND_TABLE : RUBBER_BAND_TABLE;
  const row = table.find((r) => r.position === position);
  if (row) return row.weights;
  const last = table[table.length - 1];
  return last?.weights ?? [0.25, 0.25, 0.25, 0.25];
}

/** Tier order matches the {@link RUBBER_BAND_TABLE} weight tuples. */
const TIERS_IN_ORDER: readonly ItemTier[] = [
  'COMMON',
  'UNCOMMON',
  'RARE',
  'LEGENDARY',
];

/** Picks a uniformly-random item from a tier's pool. */
export function pickItemFromTier(
  tier: ItemTier,
  rng: () => number = Math.random,
): ItemId {
  const pool = ITEMS_BY_TIER[tier];
  if (pool.length === 0) return 'COIN_STASH';
  const idx = Math.floor(rng() * pool.length);
  return pool[idx] ?? 'COIN_STASH';
}

/**
 * Rolls one item by weighted tier. When {@code excludeTier} is set,
 * that tier's weight is redistributed proportionally to the others —
 * used by the legendary-cap reroll path.
 */
function rollOneItem(
  weights: readonly [number, number, number, number],
  excludeTier: ItemTier | null,
  rng: () => number,
): ItemId {
  let effective: readonly [number, number, number, number] = weights;
  if (excludeTier !== null) {
    const idx = TIERS_IN_ORDER.indexOf(excludeTier);
    if (idx >= 0) {
      const removed = weights[idx] ?? 0;
      const rest = (weights[0] ?? 0) + (weights[1] ?? 0) + (weights[2] ?? 0) + (weights[3] ?? 0) - removed;
      if (rest > 0) {
        const scale = (rest + removed) / rest;
        const next: number[] = [0, 0, 0, 0];
        for (let i = 0; i < 4; i++) {
          next[i] = i === idx ? 0 : (weights[i] ?? 0) * scale;
        }
        effective = [next[0]!, next[1]!, next[2]!, next[3]!] as const;
      }
    }
  }
  const r = rng();
  let cumulative = 0;
  for (let i = 0; i < TIERS_IN_ORDER.length; i++) {
    cumulative += effective[i] ?? 0;
    if (r < cumulative) {
      return pickItemFromTier(TIERS_IN_ORDER[i] ?? 'COMMON', rng);
    }
  }
  // Floating-point edge — fall through to common.
  return pickItemFromTier('COMMON', rng);
}

/**
 * Optional knobs for {@link rollLoadoutForPosition}. Kept as an
 * options bag (rather than positional args) so future flags don't
 * push the signature into ambiguity.
 */
export interface RollLoadoutOptions {
  /** Random source. Defaults to {@code Math.random}; tests pass a
   *  deterministic seed for reproducible rolls. */
  rng?: () => number;
  /** When true, consult {@link FINAL_LAP_RUBBER_BAND_TABLE} instead of
   *  the standard rubber-band — every player guaranteed Rare-or-better.
   *  Phase-3 callers compute this from {@code matchNumber} via
   *  {@link isFinalLap}. */
  finalLap?: boolean;
}

/**
 * Rolls a 3-item loadout for a player at the given standing.
 *
 * <p>Applies {@link MAX_LEGENDARIES_PER_ROLL}: second and third
 * legendary draws are re-rolled from the non-legendary distribution
 * (with proportional weight redistribution) so a single hot streak
 * can't grant a player three Heists in one round.
 *
 * <p>When {@code options.finalLap} is true, the Final Lap rubber-band
 * table is used — see {@link FINAL_LAP_RUBBER_BAND_TABLE}. The cap
 * still applies, so even a 4th-place Final Lap roll caps at one
 * legendary; the other two slots come from the Rare pool.
 */
export function rollLoadoutForPosition(
  position: number,
  options: RollLoadoutOptions = {},
): readonly [ItemId, ItemId, ItemId] {
  const rng = options.rng ?? Math.random;
  const finalLap = options.finalLap ?? false;
  const weights = tierWeightsForPosition(position, finalLap);
  let legendaries = 0;
  const out: ItemId[] = [];
  for (let i = 0; i < 3; i++) {
    let item = rollOneItem(weights, null, rng);
    if (ITEMS[item].tier === 'LEGENDARY') {
      if (legendaries >= MAX_LEGENDARIES_PER_ROLL) {
        item = rollOneItem(weights, 'LEGENDARY', rng);
      } else {
        legendaries++;
      }
    }
    out.push(item);
  }
  return [out[0] ?? 'COIN_STASH', out[1] ?? 'COIN_STASH', out[2] ?? 'COIN_STASH'] as const;
}

/**
 * True when a player at this standing has any chance of rolling a
 * legendary. Drives the "legendary shimmer" telegraph on their reels.
 *
 * <p>During Final Lap (round 7+), every position has Legendary weight,
 * so the shimmer telegraph fires for every seat — a deliberate signal
 * that the chaos floor has just risen.
 */
export function isUnderdogPosition(position: number, finalLap = false): boolean {
  const weights = tierWeightsForPosition(position, finalLap);
  return (weights[3] ?? 0) > 0;
}
